<script>
    import I18n from '@js/modules/translations';

    import {mapStore} from '@js/stores/mapStore';
    import {userStore} from '@js/stores/userStore';

    import {showProductModal} from '@js/actions/basketActions';
    import {MILE_TO_METERS} from '@js/components/modules/constants';

    import AnalyticsService from '@js/modules/analyticsService';
    import Notification from '@js/components/layouts/notification';

    // Route-generation mode chosen before drawing — mirrors the store so the map (isochrone effect +
    // loop generate button) stays in sync. One active at a time so it's clear what's on.
    const mode = $derived($mapStore.plannerMode);

    // distance / duration within the active mode
    let selectedType = $state('distance');

    const isochronePresets = $derived($userStore.featureFlags[`map_isochrone_${selectedType}`] || []);
    const loopPresets = $derived($userStore.featureFlags[`map_round_trip_${selectedType}`] || []);
    const presets = $derived(mode === 'isochrone' ? isochronePresets : (mode === 'loop' ? loopPresets : []));

    // Highlight the applied preset only when its type matches the current toggle.
    const appliedType = $derived(mode === 'isochrone' ? $mapStore.isochroneType : $mapStore.roundTripType);
    const appliedValue = $derived(mode === 'isochrone' ? $mapStore.isochroneValue : $mapStore.roundTripValue);

    const hasStartPoint = $derived(!!$mapStore.startPointLocation);

    const _formatDistance = (meters) => {
        const isMi = $userStore.unit === 'mi';
        const value = Math.round(isMi ? meters / MILE_TO_METERS : meters / 1000);

        return `${value} ${I18n.t(`js.metrics.${isMi ? 'mi' : 'km'}.distance_unit_k`)}`;
    };

    const _formatDuration = (seconds) => {
        const minutes = Math.round(seconds / 60);

        if (minutes < 60) {
            return `${minutes} ${I18n.t('js.maps.toolbar.round_trip.unit_minutes')}`;
        }

        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        const hourLabel = I18n.t('js.maps.toolbar.round_trip.unit_hours');

        return rest ? `${hours} ${hourLabel} ${rest}` : `${hours} ${hourLabel}`;
    };

    const _formatPreset = (value) => (selectedType === 'duration' ? _formatDuration(value) : _formatDistance(value));

    const _clearIsochrone = () => {
        mapStore.isochroneType = null;
        mapStore.isochroneValue = 0;
    };

    const _applyIsochrone = (value) => {
        if (!value) {
            return;
        }

        mapStore.isochroneType = selectedType;
        mapStore.isochroneValue = value;

        AnalyticsService.trackIsochroneSelected(selectedType, value);

        if (!hasStartPoint) {
            Notification.info(I18n.t('js.maps.planner_options.isochrone_help'));
        }
    };

    const _applyLoop = (value) => {
        mapStore.roundTripType = selectedType;
        mapStore.roundTripValue = value ?? null;
    };

    // Selecting the active mode again toggles back to free drawing.
    const _selectMode = async (next) => {
        const target = next === mapStore.plannerMode ? 'free' : next;

        // A reachable zone is traced from a fresh start point — a route already on the map leaves no
        // clean way to place it, so offer to clear the route first (cancel keeps the current mode).
        if (target === 'isochrone' && mapStore.map?.hasDrawnRoute?.()) {
            const {default: Swal} = await import(/* webpackChunkName: "theme-swal" */ '@js/modules/swal');

            const result = await Swal.fire({
                title:             I18n.t('js.maps.planner_options.isochrone_clear_confirm'),
                icon:              'warning',
                showCancelButton:  true,
                confirmButtonText: I18n.t('js.maps.planner_options.clear'),
                cancelButtonText:  I18n.t('js.helpers.buttons.cancel')
            });

            if (!result.isConfirmed) {
                return;
            }

            // Already confirmed above — skip clearMap's own popup; its clear() resets the route/stats.
            mapStore.map.clearMap(() => {
                mapStore.startPointLocation = undefined;
                window.dispatchEvent(new CustomEvent('mapplanner:map-cleared'));
            }, {noPopup: true});
        }

        mapStore.plannerMode = target;

        // Isochrone is drawn from the store; drop it whenever we leave that mode.
        if (target !== 'isochrone') {
            _clearIsochrone();
        }

        if (target === 'isochrone') {
            _applyIsochrone(isochronePresets[0]);
        } else if (target === 'loop') {
            _applyLoop(loopPresets[0]);
        }
    };

    const _selectType = (type) => {
        selectedType = type;

        if (mode === 'isochrone') {
            _applyIsochrone(($userStore.featureFlags[`map_isochrone_${type}`] || [])[0]);
        } else if (mode === 'loop') {
            _applyLoop(($userStore.featureFlags[`map_round_trip_${type}`] || [])[0]);
        }
    };

    const _pickPreset = (value) => {
        if (mode === 'isochrone') {
            _applyIsochrone(value);
        } else if (mode === 'loop') {
            _applyLoop(value);
        }
    };

    const _showPaywall = () => {
        const gated = mode === 'isochrone'
            ? (selectedType === 'duration' ? 'map_isochrone_duration' : 'map_isochrone_distance')
            : (selectedType === 'duration' ? 'map_round_trip_duration' : 'map_round_trip_distance');

        showProductModal(gated);
    };

    const MODES = [
        {key: 'free', icon: 'M3,3H21V5H3V3M3,7H15V9H3V7M3,11H21V13H3V11M3,15H15V17H3V15M3,19H21V21H3V19Z'},
        {key: 'isochrone', icon: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7Z'},
        {key: 'loop', icon: 'M12,4C14.1,4 16.1,4.8 17.6,6.3C20.7,9.4 20.7,14.5 17.6,17.6C15.8,19.5 13.3,20.2 10.9,19.9L11.4,17.9C13.1,18.1 14.9,17.5 16.2,16.2C18.5,13.9 18.5,10.1 16.2,7.7C15.1,6.6 13.5,6 12,6V10.6L7,5.6L12,0.6V4M6.3,17.6C3.7,15 3.3,11 5.1,7.9L6.6,9.4C5.5,11.6 5.9,14.4 7.8,16.2C8.3,16.7 8.9,17.1 9.6,17.4L9,19.4C8,19 7.1,18.4 6.3,17.6Z'}
    ];

    const _guidance = $derived.by(() => {
        if (mode === 'isochrone') {
            return hasStartPoint
                ? I18n.t('js.maps.planner_options.isochrone_active')
                : I18n.t('js.maps.planner_options.isochrone_help');
        }

        if (mode === 'loop') {
            return hasStartPoint
                ? I18n.t('js.maps.planner_options.loop_active')
                : I18n.t('js.maps.planner_options.loop_help');
        }

        return '';
    });
</script>

<div class="planner-route-options mx-auto mb-6 w-[94%] max-w-4xl rounded-xl bg-white px-4 py-3 block-shadow-xl md:px-6">
    <span class="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-500">
        {I18n.t('js.maps.planner_options.title')}
    </span>

    <!-- Exclusive mode selector: free draw / reachable zone / loop -->
    <div class="flex flex-wrap gap-2" role="group" aria-label={I18n.t('js.maps.planner_options.title')}>
        {#each MODES as item (item.key)}
            <button type="button"
                    class="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors"
                    class:border-primary-600={mode === item.key}
                    class:bg-primary-50={mode === item.key}
                    class:text-primary-700={mode === item.key}
                    class:border-gray-200={mode !== item.key}
                    class:bg-white={mode !== item.key}
                    class:text-gray-600={mode !== item.key}
                    aria-pressed={mode === item.key}
                    onclick={() => _selectMode(item.key)}>
                <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d={item.icon}/>
                </svg>
                {I18n.t(`js.maps.planner_options.${item.key}`)}
            </button>
        {/each}
    </div>

    {#if mode !== 'free'}
        <div class="mt-3 border-t border-gray-100 pt-3">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
                <!-- distance vs duration -->
                <div class="flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold">
                    <button type="button"
                            class="rounded-md px-3 py-1.5 transition-colors"
                            class:bg-white={selectedType === 'distance'}
                            class:text-primary-700={selectedType === 'distance'}
                            class:shadow-sm={selectedType === 'distance'}
                            class:text-gray-500={selectedType !== 'distance'}
                            aria-pressed={selectedType === 'distance'}
                            onclick={() => _selectType('distance')}>
                        {I18n.t('js.maps.toolbar.round_trip.distance_label')}
                    </button>
                    <button type="button"
                            class="rounded-md px-3 py-1.5 transition-colors"
                            class:bg-white={selectedType === 'duration'}
                            class:text-primary-700={selectedType === 'duration'}
                            class:shadow-sm={selectedType === 'duration'}
                            class:text-gray-500={selectedType !== 'duration'}
                            aria-pressed={selectedType === 'duration'}
                            onclick={() => _selectType('duration')}>
                        {I18n.t('js.maps.toolbar.round_trip.duration_label')}
                    </button>
                </div>

                {#if presets.length}
                    <div class="flex flex-wrap gap-2">
                        {#each presets as preset (preset)}
                            <button type="button"
                                    class="rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors"
                                    class:border-primary-600={appliedType === selectedType && appliedValue === preset}
                                    class:bg-primary-50={appliedType === selectedType && appliedValue === preset}
                                    class:text-primary-700={appliedType === selectedType && appliedValue === preset}
                                    class:border-gray-200={!(appliedType === selectedType && appliedValue === preset)}
                                    class:bg-white={!(appliedType === selectedType && appliedValue === preset)}
                                    class:text-gray-600={!(appliedType === selectedType && appliedValue === preset)}
                                    aria-pressed={appliedType === selectedType && appliedValue === preset}
                                    onclick={() => _pickPreset(preset)}>
                                {_formatPreset(preset)}
                            </button>
                        {/each}
                    </div>
                {:else}
                    <button type="button"
                            class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 cursor-pointer hover:bg-amber-100"
                            onclick={_showPaywall}>
                        🔒 {I18n.t('js.maps.toolbar.round_trip.unavailable')}
                    </button>
                {/if}
            </div>

            {#if _guidance}
                <p class="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                    <svg class="h-3.5 w-3.5 shrink-0 text-primary-800" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                    </svg>
                    {_guidance}
                </p>
            {/if}
        </div>
    {/if}
</div>
